/**
 * Notification data hooks.
 *
 * - useUnreadNotificationCount — 60s poll for the bell badge (silent-fail,
 *   same non-critical pattern as useSidebarBadges).
 * - useNotificationList — fetched on drawer open, paged.
 * - markNotificationRead / markAllNotificationsRead — mutations returning the
 *   fresh unread count so the bell updates instantly.
 */

import { useState, useEffect, useCallback } from 'react'
import { authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import type { ApiResponse } from '@/lib/permissions'
import type { NotificationItem, NotificationListData, UnreadCountData } from '../types'

const POLL_INTERVAL = 60000 // 60 seconds

export function useUnreadNotificationCount() {
  const [unreadCount, setUnreadCount] = useState(0)

  const refresh = useCallback(async () => {
    const token = useAuthStore.getState().tokens?.accessToken
    if (!token) return
    try {
      const resp = await authFetch<ApiResponse<UnreadCountData>>(
        '/api/v1/notifications/unread-count',
        token,
      )
      if (resp.success && resp.data) setUnreadCount(resp.data.unreadCount)
    } catch {
      // Silently fail — the badge is non-critical
    }
  }, [])

  useEffect(() => {
    // Deferred initial fetch (setTimeout 0) keeps the React Compiler's
    // set-state-in-effect rule happy — the badge is async by nature anyway.
    const initial = setTimeout(refresh, 0)
    const interval = setInterval(refresh, POLL_INTERVAL)
    return () => {
      clearTimeout(initial)
      clearInterval(interval)
    }
  }, [refresh])

  return { unreadCount, refresh, setUnreadCount }
}

const PAGE_SIZE = 50

export function useNotificationList(open: boolean) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [serverUnreadCount, setServerUnreadCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const token = useAuthStore((s) => s.tokens?.accessToken)

  const fetchPage = useCallback(
    async (pageToLoad: number, replace: boolean) => {
      if (!token) return
      setIsLoading(true)
      try {
        const resp = await authFetch<ApiResponse<NotificationListData>>(
          `/api/v1/notifications?page=${pageToLoad}&size=${PAGE_SIZE}`,
          token,
        )
        if (resp.success && resp.data) {
          const incoming = resp.data.notifications
          setNotifications((prev) => {
            if (replace) return incoming
            // Audit P5: accumulate pages; guard against duplicates on refresh
            const seen = new Set(prev.map((n) => n.notificationId))
            return [...prev, ...incoming.filter((n) => !seen.has(n.notificationId))]
          })
          setServerUnreadCount(resp.data.unreadCount)
          setHasMore(resp.data.hasMore)
          setPage(pageToLoad)
        }
      } catch {
        if (replace) setNotifications([])
      } finally {
        setIsLoading(false)
      }
    },
    [token],
  )

  const refetch = useCallback(() => fetchPage(1, true), [fetchPage])
  const loadMore = useCallback(() => fetchPage(page + 1, false), [fetchPage, page])

  // Fetch fresh every time the drawer opens.
  useEffect(() => {
    if (open) refetch()
  }, [open, refetch])

  return {
    notifications,
    isLoading,
    serverUnreadCount,
    hasMore,
    refetch,
    loadMore,
    setNotifications,
    setServerUnreadCount,
  }
}

export async function markNotificationRead(notificationId: string): Promise<number | null> {
  const token = useAuthStore.getState().tokens?.accessToken
  if (!token) return null
  try {
    const resp = await authFetch<ApiResponse<UnreadCountData>>(
      `/api/v1/notifications/${encodeURIComponent(notificationId)}/read`,
      token,
      { method: 'PATCH' },
    )
    return resp.success && resp.data ? resp.data.unreadCount : null
  } catch {
    return null
  }
}

export async function markAllNotificationsRead(): Promise<number | null> {
  const token = useAuthStore.getState().tokens?.accessToken
  if (!token) return null
  try {
    const resp = await authFetch<ApiResponse<UnreadCountData>>(
      '/api/v1/notifications/read-all',
      token,
      { method: 'POST' },
    )
    return resp.success && resp.data ? resp.data.unreadCount : null
  } catch {
    return null
  }
}
